import { forwardRef, Fragment, useMemo } from 'react';

import { Tooltip } from 'react-tooltip';

import classNames from 'classnames';

import {
  FontAwesomeIcon,
  If,
  Loader,
  MoneyFormat,
  NumberFormat,
  PercentFormat,
  Switch,
  Then,
} from '@hsp/react-component-library';
import { format as formatNumber, formatMoney, formatPercent, isDefined } from '@hsp/utilities';

import { IBoxProps } from 'types/components/stat-box';

import cssClasses from './box.module.scss';

const Box = forwardRef<HTMLDivElement, IBoxProps>(
  ({ className, data: { count, title, description, type = 'number' }, loading = false, size, ...props }, ref) => {
    const isSmall = useMemo(() => size === 'sm', [size]);
    const tooltip = useMemo(() => {
      switch (type) {
        case 'money':
          return formatMoney(count);

        case 'number':
          return formatNumber(count);

        case 'percent':
          return formatPercent(count);
      }
    }, [count, type]);

    return (
      <Fragment>
        <div
          {...props}
          className={classNames(className, cssClasses.box, {
            [cssClasses.sm]: isSmall,
          })}
          data-tooltip-content={isSmall ? `${title}: ${tooltip}` : undefined}
          data-tooltip-id={isSmall ? 'global-tool-tip' : undefined}
          data-tooltip-place="bottom"
          ref={ref}>
          <Loader
            hideChildren={false}
            loading={loading}
            ref={ref}>
            <div
              className={classNames(cssClasses.count, 'd-flex gap-2', { [cssClasses.sm]: isSmall }, 'text-truncate')}>
              <Switch condition={(value) => value === type}>
                <Switch.Case value={'money'}>
                  <MoneyFormat
                    negativeClassName="text-danger"
                    value={count}
                  />
                </Switch.Case>
                <Switch.Case value={'number'}>
                  <NumberFormat value={count} />
                </Switch.Case>
                <Switch.Case value={'percent'}>
                  <PercentFormat value={count} />
                </Switch.Case>
              </Switch>
              <If condition={isDefined(description)}>
                <Then>
                  <a data-tooltip-id={title}>
                    <FontAwesomeIcon
                      icon={['fas', 'circle-info']}
                      size="2xs"
                    />
                  </a>
                </Then>
              </If>
            </div>
            <div className="text-truncate title">{title}</div>
            <If condition={isDefined(description)}>
              <Then>
                <Tooltip id={title}>
                  <div className="text-truncate description">{description}</div>
                </Tooltip>
              </Then>
            </If>
          </Loader>
        </div>
      </Fragment>
    );
  }
);

Box.displayName = 'Box';

export default Box;
